import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useController } from '@rest-hooks/react';
import { useParams } from 'react-router-dom';

import PaymentProviderResource from 'resources/Profile/PaymentProviderResource';
import OrderResource from 'resources/user/OrderResource';

import CartPaymentCollection from './CartPaymentCollection';
import PaymentProcessed from 'components/CheckoutModal/PaymentModal/PaymentProcessed';
import TicketDetails from 'components/CheckoutModal/PaymentModal/TicketDetails';
import TicketsSent from 'components/CheckoutModal/PaymentModal/TicketsSent';
import InvoiceDetails from 'components/CheckoutModal/PaymentModal/InvoiceDetails';
import InvoiceCreated from 'components/CheckoutModal/PaymentModal/InvoiceCreated';
import RepeatPurchaseModal from 'components/CheckoutModal/RepeatPurchaseModal';

import Confetti from 'components/Confetti';
import StyledDialog from 'ui/StyledComponents/StyledDialog';
import Button from 'ui/Button';
import Description from 'ui/text/Description';

import { useError } from 'utils/useErrorController';
import { useCart } from 'utils/CartContext';
import useSliderController from 'utils/useSliderController';
import isMobileDevice from 'utils/mobileAndTabletCheck';
import { checkLoginStatus } from 'utils/checkLoginStatus';

import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

import { t } from 'i18n/index';

const stripeDefaultState = {
  clientSecret: "",
  stripePublishableKey: "",
  stripeAccountId: "",
};

const orderDefaultState = {
  paymentIntentId: "",
  selectedProvider: "",
  providers: [],
};

const createAddToCartGAData = (cartItems) => ({
  currency: cartItems[0].product.currency,
  value: cartItems.reduce((sum, ci) => {
    const price = ci.product.unit_price + ci.product.application_fee;
    return sum + (price / 100) * ci.amount;
  }, 0),
  items: cartItems.map(ci => ({
    item_id: ci.product.id?.toString(),
    item_name: ci.product.title,
    item_category: ci.product.category,
    price: (ci.product.unit_price + ci.product.application_fee) / 100,
    quantity: ci.amount,
  })),
});

const createAddToCartFBData = (cartItems) => ({
  content_ids: cartItems.map(ci => ci.product.id?.toString()),
  content_type: 'product',
  contents: cartItems.map(ci => ({
    id: ci.product.id?.toString(),
    quantity: ci.amount,
  })),
  currency: cartItems[0].product.currency,
  value: cartItems.reduce((sum, ci) => {
    const price = ci.product.unit_price + ci.product.application_fee;
    return sum + (price / 100) * ci.amount;
  }, 0),
});

const CartCheckoutModal = ({
  onClose,
}) => {

  const [step, setStep] = useState(1);
  const [orderState, setOrderState] = useState(orderDefaultState);
  const [stripeState, setStripeState] = useState(stripeDefaultState);
  const [content, setContent] = useState(null);
  const [userPhone, setUserPhone] = useState('');
  const [openRepeatPurchaseModal, setOpenRepeatPurchaseModal] = useState(false);
  const [cartRejectionError, setCartRejectionError] = useState(null);
  const [purchasedItems, setPurchasedItems] = useState(null);

  const { cartItems, clearCart } = useCart();
  const { fetch } = useController();
  const { organizationSlug } = useParams();
  const { handleError, setLoading } = useError();

  // Post-success screens still need the purchased items after we clear the
  // cart. Snapshot on transition to step 2, then read from the snapshot.
  const displayItems = purchasedItems || cartItems;

  useEffect(() => {
    if (step >= 2 && !purchasedItems) {
      setPurchasedItems(cartItems);
      clearCart();
    }
  }, [step, purchasedItems, cartItems, clearCart]);

  const totalAmount = displayItems.reduce((sum, ci) => sum + ci.amount, 0);
  const suffix = totalAmount > 1 ? "s" : "";

  // A cart can now hold tickets and/or non-ticket products. Ticket-only
  // post-purchase steps (ticket details/distribution) must run against the
  // ticket items alone, and a product-only cart skips them entirely.
  const ticketItems = displayItems
    .map((ci, orderItemIndex) => ({ ...ci, orderItemIndex }))
    .filter((ci) => ci.product?.category === 'ticket');
  const cartHasTickets = ticketItems.length > 0;
  const ticketAmount = ticketItems.reduce((sum, ci) => sum + ci.amount, 0);

  const { openSliderComponent, handleImageClick, SliderComponent } = useSliderController(
    t('profile:memberships:claimModal:atTheDoorModalTitle', { suffix }),
    t('profile:memberships:claimModal:atTheDoorModalDescription', { suffix })
  );

  const showStep2 = step === 2 && !openSliderComponent;
  const searchParams = new URLSearchParams(window.location.search);
  const atTheDoor = searchParams.has('openTicket');

  const handleShowTickets = (uuids) => {
    if (atTheDoor) {
      handleImageClick(uuids, 0);
    }
  };

  const handleClaimReset = () => {
    // Cart is cleared on the step >= 2 transition via the effect above, so we
    // only need to reset local modal state here.
    setStripeState(stripeDefaultState);
    setOrderState(orderDefaultState);
    setUserPhone('');
    setStep(1);
    setCartRejectionError(null);
    setPurchasedItems(null);
    onClose();
  };

  const handleProviderSelection = (provider, availableProviders) => {
    // Mirror maushabitos PayPal injection from the single-item checkout flow.
    // The providers endpoint does not include PayPal; it is injected client-side
    // for this specific org (the cart only holds products, never passes, so the
    // isPass guard from single-item checkout is irrelevant here).
    if (organizationSlug === "maushabitos") {
      if (!availableProviders.includes("paypal")) {
        availableProviders.push("paypal");
      }
    }
    setOrderState((prev) => ({ ...prev, selectedProvider: provider, providers: availableProviders }));
  };

  const fetchAvailablePaymentMethods = async () => {
    try {
      setLoading(true);

      const { providers } = await fetch(PaymentProviderResource.detail(), {
        organization_slug: organizationSlug,
        id: "payment_providers",
      });

      if (providers.includes("eupago")) {
        handleProviderSelection("eupago", providers);
      } else if (providers.includes("stripe")) {
        handleProviderSelection("stripe", providers);
      } else {
        handleError(t("errors:noPaymentProviders"));
      }
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const initCheckout = async () => {
    // Track add_to_cart
    if (window.trackEvent && cartItems.length > 0) {
      const ga_data = createAddToCartGAData(cartItems);
      const fb_data = createAddToCartFBData(cartItems);
      window.trackEvent('add_to_cart', ga_data, fb_data);
    }

    // Partial-auth users (lite accounts, no password) can reach this modal
    // but OrdersController#index requires full JWT auth and would 401 them
    // — and a 401 triggers localStorage.clear, logging the user out. Skip
    // the repeat-purchase warning for them and go straight to payment.
    if (!checkLoginStatus()) {
      await fetchAvailablePaymentMethods();
      return;
    }

    const cartItemsSignature = JSON.stringify(cartItems.map((ci) => ({
      item_id: ci.product.id,
      product_variant_id: ci.variant?.id,
      amount: ci.amount,
      event_product_listing_id: ci.product.event_product_listing_id,
    })));

    try {
      const recentOrders = await fetch(OrderResource.list(), {
        last_hour: true,
        cart_items: cartItemsSignature,
      });

      if (recentOrders.length > 0) {
        setOpenRepeatPurchaseModal(true);
      } else {
        await fetchAvailablePaymentMethods();
      }
    } catch (error) {
      handleError(error);
    }
  };

  const handleRepeatPurchaseConfirm = async () => {
    setOpenRepeatPurchaseModal(false);
    await fetchAvailablePaymentMethods();
  };

  const closeRepeatPurchaseModal = () => {
    setOpenRepeatPurchaseModal(false);
    handleClaimReset();
  };

  // Parent ensures the user is authenticated before mounting this component,
  // so the init flow runs once on mount.
  useEffect(() => {
    if (cartItems.length > 0) initCheckout();
  }, []);

  const isMobile = isMobileDevice();
  const showLargeModal = !isMobile && step === 1;
  const modalWidth = showLargeModal ? "md" : "sm";

  if (displayItems.length === 0) return null;

  return (
    <>
      {showStep2 && <Confetti />}

      <StyledDialog open fullWidth maxWidth={modalWidth} hideBackdrop={step === 2}>
        <IconButton onClick={handleClaimReset} aria-label='close'>
          <CloseIcon />
        </IconButton>
        <DialogContent>
          {cartRejectionError && (
            <div className='payment-processed'>
              <Description>{cartRejectionError}</Description>
              <div className='button-group'>
                <Button onClick={() => { clearCart(); handleClaimReset(); }} fullwidth secondary>
                  {t('literal:close')}
                </Button>
              </div>
            </div>
          )}

          {!cartRejectionError && step === 1 && orderState.selectedProvider && (
            <CartPaymentCollection
              cartItems={cartItems}
              userPhone={userPhone}
              onUserPhoneChange={setUserPhone}
              stripeState={stripeState}
              onStripeStateChange={setStripeState}
              orderState={orderState}
              onOrderChange={setOrderState}
              onStepChange={setStep}
              onContentChange={setContent}
              onShowTickets={handleShowTickets}
            />
          )}

          {showStep2 && (
            <PaymentProcessed
              setStep={setStep}
              handleClaimReset={handleClaimReset}
              stripe_purchase={true}
              isTicket={cartHasTickets}
              amount={cartHasTickets ? ticketAmount : totalAmount}
              content={content}
              organizationTitle={displayItems[0]?.product?.organization?.organization_title}
              atTheDoor={atTheDoor}
            />
          )}

          {step === 3 && (
            <TicketDetails
              setStep={setStep}
              handleClaimReset={handleClaimReset}
              paymentIntentId={orderState.paymentIntentId}
              amount={ticketAmount}
              cartItems={ticketItems}
            />
          )}

          {step === 4 && (
            <TicketsSent
              setStep={setStep}
              handleClaimReset={handleClaimReset}
            />
          )}

          {step === 5 && (
            <InvoiceDetails
              setStep={setStep}
              senderName={displayItems[0]?.product?.participant?.name}
              senderEmail={displayItems[0]?.product?.participant?.email}
              paymentIntentId={orderState.paymentIntentId}
            />
          )}

          {step === 6 && (
            <InvoiceCreated handleClaimReset={handleClaimReset} />
          )}
        </DialogContent>
      </StyledDialog>

      <RepeatPurchaseModal
        open={openRepeatPurchaseModal}
        onConfirm={handleRepeatPurchaseConfirm}
        onClose={closeRepeatPurchaseModal}
        objectCategory={cartHasTickets ? "ticket" : "product"}
        isCart
      />

      <SliderComponent />
    </>
  );
};

CartCheckoutModal.propTypes = {
  onClose: PropTypes.func,
};

export default CartCheckoutModal;
